<script lang="ts" setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";

import {type PageState} from 'primevue/paginator';
import {isDesktop} from "@/helpers/breakpointHelpers";
import FilterSection from "@/components/filters/FilterSection.vue";

import {Column, DataTable} from "primevue";
import {useModalsStore} from "@/stores/modals";
import {useCartStore} from "@/stores/cart";
import CartInput from "@/components/cart/CartInput.vue";
import {price} from "@/filters/price";
import {useFilterStore} from "@/stores/filter";
import {cloneDeep, merge} from "lodash";

import qs from 'qs';

const modalStore = useModalsStore()
const cartStore = useCartStore()
const filterStore = useFilterStore()

type TFilter = {
  title: string,
  handle: string,
  values: string[]
}

type TProps = {
  fields: {
    title: string,
    handle: string
  }[],
  filters: TFilter[],
  products: any[],
  filterEnabled?: boolean
}

const props = withDefaults(defineProps<TProps>(), {
  filterEnabled: false
})

const enabledFilters = computed(() => {
  const enabledFilters: { [key: string]: string[] } = {}

  computedProducts.value.forEach((product) => {
    Object.keys(filters.value).forEach((filter) => {
      if (!product[filter]) {
        return
      }

      if (typeof enabledFilters[filter] === 'undefined') {
        enabledFilters[filter] = []
      }

      enabledFilters[filter].push(product[filter])
    })
  })

  return enabledFilters
})

const excludeFieldsFromFilter = ['sku', 'price', 'availableCount', 'section']
const productsPerPage = ref(20)
const page = ref(1)

const displayingFields = computed(() => {
  let fields = Object.values(props.fields)

  fields = fields.sort((a, b) => {
    if (a.handle === 'price') {
      return 1;
    }

    if (b.handle === 'price') {
      return -1;
    }

    if (a.handle !== 'sku') {
      return 1;
    }

    if (b.handle !== 'sku') {
      return -1;
    }

    return 0;
  })

  return fields.filter((field) => {
    return props.products.some((product) => {
      return typeof product[field.handle] !== 'undefined' && product[field.handle]
    })
  })
})

const defaultFilter: { [key: string]: Set<string> } = {}

props.filters.forEach((filter) => {
  defaultFilter[filter.handle] = new Set()
})

defaultFilter['category'] = filterStore.selectedFilter
const filters = ref<{ [key: string]: Set<string> }>(defaultFilter)

watch(() => filterStore.isChanged, () => {
  filters.value.category = filterStore.selectedFilter
  filterParams.value = merge(filterParams.value, filters.value)
})

const rowsPerPageOptions = computed(() => {
  return [...new Set([10, 20, 30, productsPerPage.value])];
})

const filterParams = ref({})

const changePageHandler = (pageEvent: PageState) => {
  page.value = pageEvent.page + 1
}

const rowsPerPageUpdateHandler = (rowsPerPage: number) => {
  productsPerPage.value = rowsPerPage
}

const fieldOptions = (handle: string) => {
  if (excludeFieldsFromFilter.includes(handle)) {
    return
  }

  const values = new Map()

  props.products.forEach((product) => {
    if (product[handle]) {
      values.set(product[handle], product[handle])
    }
  })

  return Array.from(values)
}

const clearFilters = () => {
  Object.values(filters.value).forEach((v) => v.clear())
}

const computedProducts = computed(() => {
  let _products = props.products

  if (!props.filterEnabled) {
    return _products
  }

  Object.keys(filters.value).forEach((filter) => {
    if (filters.value[filter].size <= 0) {
      return
    }

    _products = _products.filter((product) => {
      return [...filters.value[filter]].includes(product[filter])
    })
  })

  return _products
})

const isFilterActive = computed(() => {
  return computedProducts.value.length !== props.products.length
})

const parseFilters = () => {
  const url = new URL(window.location.href)
  const search = window.location.search
  const _parsedFilters = qs.parse(search)
  const _filters = cloneDeep(filters.value)

  if (url.searchParams.has('q')) {
    // @ts-ignore
    filterParams.value.q = url.searchParams.get('q')
  }

  Object.values(_filters).forEach((v) => v.clear())

  filterStore.selectedFilter.clear()

  Object.keys(_parsedFilters).forEach((key) => {
    const _key = key.startsWith('?') ? key.slice(1) : key

    if (_key === 'page') {
      page.value = +(_parsedFilters[key] ?? 1)

      return
    }

    if (_key === 'productsPerPage') {
      productsPerPage.value = +(_parsedFilters[key] ?? 20)

      return
    }

    if (_key === 'category') {
      // @ts-ignore
      _parsedFilters[key].forEach((v) => {
        filterStore.toggleFilter(v, false, true)
      })
    }

    _filters[_key] = new Set(_parsedFilters[key] as string[])
  })

  if (!Object.keys(_parsedFilters).includes('page') && !Object.keys(_parsedFilters).includes('?page')) {
    page.value = 1
  }

  if (!Object.keys(_parsedFilters).includes('productsPerPage') && !Object.keys(_parsedFilters).includes('?productsPerPage')) {
    productsPerPage.value = 20
  }

  filters.value = _filters
}

window.addEventListener('popstate', function () {
  setTimeout(() => {
    parseFilters()
  }, 0)
})

watch(page, () => {
  // @ts-ignore
  filterParams.value.page = page.value
})

watch(productsPerPage, () => {
  // @ts-ignore
  filterParams.value.productsPerPage = productsPerPage.value
})

watch(filterParams.value, () => {
  const url = new URL(window.location.href)

  url.search = qs.stringify(filterParams.value, {
    filter: (_, object) => object instanceof Set ? [...object] : object,
  })

  if (url.href === window.location.href) {
    return
  }

  window.history.pushState({}, '', url.href)
})

const toggleFilter = (e: { handle: string, value: string }) => {
  if (typeof filters.value[e.handle] === 'undefined') {
    filters.value[e.handle] = new Set()
  }

  if (filters.value[e.handle].has(e.value)) {
    filters.value[e.handle].delete(e.value)

    return
  }

  filters.value[e.handle].add(e.value)

  filterParams.value = merge(filterParams.value, filters.value)
}

const isMobile = ref(!isDesktop())

const resizeHandler = (): void => {
  isMobile.value = !isDesktop()
}

const isFilterSelected = (handle: string, value: string) => {
  if (typeof filters.value[handle as string] === 'undefined') {
    return false
  }

  return filters.value[handle as string].has(value)
}

onMounted(() => {
  window.addEventListener('resize', resizeHandler)

  setTimeout(() => {
    parseFilters()
  }, 0)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeHandler)
})

const computedFirstRow = computed(() => {
  return page.value * productsPerPage.value - productsPerPage.value
})

const isEnabled = (handle: string, value: string) => {
  return computedProducts.value.some((product) => product[handle] === value)
}

const shownFullfilterType = ref(false)

const computedfilterTypes = computed(() => {
  if (!shownFullfilterType.value) {
    return fieldOptions('filterType')?.slice(0, 9)
  }

  return fieldOptions('filterType')
})

const availablefilterTypesCount = computed<number>(() => {
  if (!shownFullfilterType.value) {
    const filterTypes = fieldOptions('filterType')?.slice(9)

    if (typeof filterTypes === 'undefined') {
      return 0
    }

    return filterTypes.reduce((sum, filterType) => {
      if (isEnabled('filterType', filterType[0])) {
        return sum + 1
      }

      return sum
    }, 0)
  }

  return 0
})
</script>

<template>
  <div class="my-[30px] gap-8 flex flex-col">
    <div v-if="props.filterEnabled" class="flex flex-row gap-4 flex-wrap justify-center">
      <button v-for="field in computedfilterTypes"
              :class="{'border-blue-600': isFilterSelected('filterType', field[0]), 'opacity-40': !isEnabled('filterType', field[0])}"
              :disabled="!isEnabled('filterType', field[0])"
              class="rounded-full px-6 py-3 border-2 hover:border-blue-600 transition-colors"
              type="button"
              @click="toggleFilter({ handle: 'filterType', value: field[0]})">
        {{ field[0] }}
      </button>
    </div>
    <button v-if="props.filterEnabled && (fieldOptions('filterType') as unknown as string[]).length > 9"
            class="hover:text-blue-600 transition-colors m-auto" type="submit"
            @click="shownFullfilterType = !shownFullfilterType">
      {{
        !shownFullfilterType ? `Показать все ${isFilterActive && (availablefilterTypesCount > 0) ? `(доступно еще ${availablefilterTypesCount})` : ''}` : 'Скрыть'
      }}
    </button>
    <div class="w-full flex flex-row gap-8">
      <div v-if="props.filterEnabled" class="flex flex-col gap-6 w-48">
        <button v-if="isFilterActive" class="hover:text-blue-600 transition-colors" type="button" @click="clearFilters">
          х Очистить фильтр
        </button>
        <div v-for="filter in props.filters">
          <FilterSection :enabled-filters="enabledFilters" :handle="filter.handle" :selected="filters[filter.handle]"
                         :title="filter.title"
                         :values="filter.values" @change="toggleFilter"/>
        </div>
      </div>
      <div class="w-full">
        <DataTable :first="computedFirstRow"
                   :rows="productsPerPage"
                   :rowsPerPageOptions="rowsPerPageOptions"
                   :value="computedProducts"
                   groupRowsBy="image"
                   paginator
                   pt:content="flex flex-row items-center"
                   pt:pcpaginator:root="paginator"
                   pt:table:class="table-auto border-collapse desktop:border text-[18px] leading-[16.2px] mb-[1.875rem] w-full"
                   rowGroupMode="rowspan"
                   @page="changePageHandler"
                   @update:rows="rowsPerPageUpdateHandler">
          <Column body-class="align-top text-center border-t p-4" field="image" header="">
            <template #body="slotProps">
              <img :src="slotProps.data.image" class="m-auto w-[200px]"/>
            </template>
          </Column>
          <Column v-for="field in displayingFields" :key="field.title"
                  :body-class="field.handle === 'sku' ? 'text-center desktop:border p-2' : 'text-center desktop:border p-2 max-desktop:hidden'"
                  :field="field.handle" :header="field.title" header-class="border p-5 uppercase">
            <template #body="slotProps">
              <component :is="field.handle === 'sku' ? 'a' : 'span'"
                         :class="field.handle === 'sku' ? 'transition-colors hover:text-[hsl(var(--roke-text-blue))]' : ''"
                         :href="slotProps.data.url" class="max-desktop:text-left">
        <span class="max-desktop:block max-desktop:text-[16px] max-desktop:leading-[16px]">
          <span v-if="field.handle === 'sku' || field.handle === 'price'"
                class="desktop:hidden">{{ field.handle === 'sku' ? 'Артикул: ' : 'Цена: ' }}</span>
          <span class="max-desktop:font-semibold">{{
              field.handle === 'price' ? price(slotProps.data[field.handle]) : slotProps.data[field.handle]
            }}</span>
        </span>
                <h2 class="desktop:hidden mt-4 !font-myriadPro text-[24px] leading-[24px]">{{
                    slotProps.data.title
                  }}</h2>
              </component>
            </template>
          </Column>
          <Column body-class="desktop:border p-2 max-desktop:hidden text-center" header="Комментарий"
                  header-class="border p-5 uppercase">
            <template #body="slotProps">
              <button aria-expanded="false" aria-haspopup="dialog" class="outline-none" data-state="closed"
                      type="button"
                      @click="modalStore.showCommentModal(slotProps.data.id)">
                <div class="relative">
                      <span class="block size-8 group" data-nimg="1">
                        <img alt="comment" class="transition-opacity bg-clip-content opacity-100 group-hover:opacity-0"
                             data-nimg="1" decoding="async" height="32" loading="lazy" src="/icons/comment.svg"
                             width="32">
                        <img alt="comment"
                             class="transition-opacity bg-clip-content absolute top-0 opacity-0 group-hover:opacity-100"
                             data-nimg="1" decoding="async" height="32" loading="lazy" src="/icons/comment_hover.svg"
                             width="32">
                      </span>
                  <div v-if="cartStore.isCommentExists(slotProps.data.id)"
                       class="size-4 rounded-full bg-[#3064C7] absolute -bottom-0.5 -right-0.5"></div>
                </div>
              </button>
            </template>
          </Column>
          <Column body-class="desktop:border p-2 max-desktop:flex max-desktop:w-full" header="Купить"
                  header-class="border p-5 uppercase">
            <template #body="slotProps">
              <div class="h-full w-full flex flex-row justify-center gap-2.5 items-center">
                <CartInput v-if="cartStore.isItemExists(slotProps.data.id)" :price="slotProps.data.price"
                           :product-id="slotProps.data.id" class="desktop:w-40"/>
                <button v-else
                        class="max-desktop:w-full desktop:w-40 rounded-[3px] h-11 desktop:h-9 inline-flex items-center whitespace-nowrap ring-offset-background transition-colors disabled:pointer-events-none disabled:opacity-50 bg-[hsl(var(--roke-text-blue))] text-white justify-center uppercase px-6 font-semibold text-xl leading-[16.2px]"
                        @click="cartStore.addToCart(slotProps.data.id, slotProps.data.price)">
                  <span class="mt-0.5">В корзину</span>
                </button>
                <button aria-expanded="false"
                        class="size-11 bg-white rounded-[3px] flex-shrink-0 flex outline-none desktop:hidden"
                        type="button" @click="modalStore.showCommentModal(slotProps.data.id)">
                  <div class="relative size-7 m-auto">
            <span class="block size-7 group" data-nimg="1">
              <img alt="comment" class="transition-opacity bg-clip-content" data-nimg="1" decoding="async" height="32"
                   loading="lazy" src="/icons/comment_hover.svg" width="32">
            </span>
                    <div v-if="cartStore.isCommentExists(slotProps.data.id)"
                         class="size-3 rounded-full bg-[#3064C7] absolute -bottom-0.5 -right-0.5"></div>
                  </div>
                </button>
                <div class="bg-white h-full flex" @click="cartStore.addToFavorite(slotProps.data.id)">
                  <div
                    class="text-[#3064C7] size-11 desktop:size-9 desktop:bg-[#F7F7F7] flex cursor-pointer group m-auto rounded-[3px]">
                    <div class="m-auto relative size-7 desktop:size-4">
                      <svg
                        :class="cartStore.isFavorite(slotProps.data.id) ? 'opacity-0 desktop:group-hover:opacity-100' : 'opacity-100 desktop:group-hover:opacity-0'"
                        class="m-auto transition-opacity absolute top-0 size-7 desktop:size-4" fill="none" height="16"
                        viewBox="0 0 18 16" width="16" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M8.63895 2.56888L8.99779 2.93783L9.3562 2.56848L10.0943 1.80786C10.0944 1.80781 10.0944 1.80777 10.0944 1.80773C11.7904 0.064048 14.5299 0.0640894 16.2258 1.80785C17.9253 3.5554 17.9239 6.38663 16.2267 8.14207C16.2265 8.14229 16.2263 8.1425 16.2261 8.14271L9.11721 15.448L9.11711 15.4482C9.04988 15.5173 8.94677 15.5173 8.87954 15.4482L1.77505 8.14311C0.074981 6.39501 0.0749858 3.55523 1.77505 1.80717L1.77505 1.80717C3.46753 0.0668963 6.20567 0.0669012 7.89811 1.80717L7.89812 1.80718L8.63895 2.56888Z"
                          fill="none" stroke="currentColor"/>
                      </svg>
                      <svg
                        :class="cartStore.isFavorite(slotProps.data.id) ? 'opacity-100 desktop:group-hover:opacity-100' : 'opacity-0 desktop:group-hover:opacity-100'"
                        class="m-auto transition-opacity absolute top-0 size-7 desktop:size-4" fill="none" height="16"
                        viewBox="0 0 18 16" width="16" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M8.63895 2.56888L8.99779 2.93783L9.3562 2.56848L10.0943 1.80786C10.0944 1.80781 10.0944 1.80777 10.0944 1.80773C11.7904 0.064048 14.5299 0.0640894 16.2258 1.80785C17.9253 3.5554 17.9239 6.38663 16.2267 8.14207C16.2265 8.14229 16.2263 8.1425 16.2261 8.14271L9.11721 15.448L9.11711 15.4482C9.04988 15.5173 8.94677 15.5173 8.87954 15.4482L1.77505 8.14311C0.074981 6.39501 0.0749858 3.55523 1.77505 1.80717L1.77505 1.80717C3.46753 0.0668963 6.20567 0.0669012 7.89811 1.80717L7.89812 1.80718L8.63895 2.56888Z"
                          fill="currentColor" stroke="currentColor"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </Column>
        </DataTable>
        <nav aria-label="pagination"
             class="mx-auto flex w-full justify-center mb-5 desktop:mb-[5rem] flex-col items-center gap-8 -translate-y-10"
             role="navigation">
          <button v-if="computedProducts.length > productsPerPage" type="button"
                  @click="productsPerPage = productsPerPage + 10">Показать еще
          </button>
        </nav>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.select {
  @apply w-96 outline-none flex flex-row items-center bg-white p-2.5 text-[#808080] font-myriadPro text-[18px] leading-[16.2px] cursor-pointer justify-between;

  &:deep([data-pc-section="overlay"]) {
    @apply bg-white;
  }

  &:deep([data-pc-section="dropdown"]) {
    @apply text-black;
  }

  &:deep([data-pc-section="label"]) {
    @apply outline-none mt-0.5;
  }
}
</style>

<style lang="scss">
.multiselect {
  @apply bg-white border;

  & [data-pc-section="header"] {
    @apply flex items-center gap-2.5 p-2.5 border-b;
  }

  & [data-pc-name="pcfiltercontainer"] {
    @apply flex items-center border py-1 px-2.5 outline-none w-full gap-2.5;
  }

  & [data-pc-section="listcontainer"] {
    @apply overflow-y-scroll;
  }

  & input {
    @apply outline-none w-full;
  }

  & [role="option"] {
    @apply flex items-center gap-2.5 text-black !opacity-100 cursor-pointer;
  }

  & [data-pc-section="optionlabel"] {
    @apply mt-0.5;
  }

  & [data-pc-section="input"] {
    @apply hidden;
  }
}

.multiselect,
.checkbox {
  & [data-pc-section="box"] {
    @apply size-3.5 border border-black;

    &:not(:empty) {
      @apply bg-[hsl(var(--roke-text-blue))];
    }

    & > svg {
      @apply hidden;
    }
  }
}

[data-pc-section="list"] {
  @apply flex flex-col gap-1 p-2 font-myriadPro bg-white;

  & > [role="option"] {
    @apply opacity-30 cursor-pointer;

    &[data-p-selected="true"],
    &:hover {
      @apply opacity-100;
    }
  }
}
</style>
